// ✅ src/components/screens/BrowseStoresScreen.js (FULL - UPDATED)
// ✅ Mobile: 2 columns
// ✅ Web: 4 columns
// ✅ Smaller cards + tighter layout
// ✅ Banner slightly taller
// ✅ Search works for stores AND products (stores that uploaded products matching search)
// ✅ Earn Money button (routes to /earn)
// ✅ REMOVED: Category filter
// ✅ FIX: Sort on mobile uses in-app Bottom Sheet (no native iOS popup)
// ✅ Contact Us button -> /contactus (ALWAYS goes there)
// ✅ NEW: Reviews + View Store are SIDE-BY-SIDE (reduces vertical card height)
// ✅ FIX: Rating pill now shows real avg from reviews collection

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}
function initials(name) {
  const s = safe(name);
  if (!s) return "S";
  const parts = s.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "S";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}

function getStoreName(s) {
  return safe(s.storeName || s.name || "Store");
}
function getStoreSlug(s) {
  return safe(s.storeSlug || s.slug);
}
function getStoreBio(s) {
  return safe(s.bio || s.description || "No description yet.");
}
function getCreatedAtMs(s) {
  const ts = s?.createdAt;
  try {
    if (!ts) return 0;
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.toDate === "function") return ts.toDate().getTime();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

/* ---------------- mobile sort sheet ---------------- */
function useIsMobile(breakpoint = 820) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function MobileSheetSelect({
  label,
  value,
  onChange,
  options, // [{value,label}]
  disabled = false,
  placeholder = "Select…",
  S,
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    options.find((o) => String(o.value) === String(value))?.label || "";

  const close = () => setOpen(false);

  return (
    <div style={{ ...S.control, padding: "10px 10px" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          width: "100%",
          border: "none",
          outline: "none",
          background: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: 13,
          fontWeight: 900,
          color: "#0f172a",
          padding: 0,
        }}
        aria-label={label}
      >
        <span style={{ color: selectedLabel ? "#0f172a" : "#64748b" }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ color: "#64748b", fontWeight: 1200 }}>▾</span>
      </button>

      {open ? (
        <div
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              background: "#fff",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              padding: 12,
              borderTop: "1px solid #e2e8f0",
              boxShadow: "0 -20px 60px rgba(15,23,42,0.15)",
              maxHeight: "70vh",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div style={{ fontWeight: 1000 }}>{label || "Select"}</div>
              <button type="button" onClick={close} style={S.linkBtn}>
                Close
              </button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {options.map((opt) => {
                const active = String(opt.value) === String(value);
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      close();
                    }}
                    style={{
                      border: "1px solid #e2e8f0",
                      background: active ? "#0f172a" : "#fff",
                      color: active ? "#fff" : "#0f172a",
                      borderRadius: 14,
                      padding: "12px 12px",
                      fontWeight: 950,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function BrowseStoresScreen() {
  const navigate = useNavigate();
  const isMobile = useIsMobile(820);

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [stores, setStores] = useState([]);

  const [qText, setQText] = useState("");
  const [sort, setSort] = useState("NEWEST");

  // ✅ storeIds that match product search
  const [productStoreIds, setProductStoreIds] = useState(() => new Set());

  // ✅ store rating map: { [storeId]: { avg: number, count: number } }
  const [ratingMap, setRatingMap] = useState({});

  // ✅ load stores
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setMsg("");

      try {
        let snap;
        try {
          const qy = query(
            collection(db, "stores"),
            orderBy("createdAt", "desc"),
            limit(200)
          );
          snap = await getDocs(qy);
        } catch {
          const qy2 = query(collection(db, "stores"), limit(200));
          snap = await getDocs(qy2);
        }

        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const filtered = rows.filter((s) => {
          const done = !!s.setupDone;
          const active = s.isActive === undefined ? true : !!s.isActive;
          return done && active;
        });

        if (!alive) return;
        setStores(filtered);
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Failed to load stores.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  // ✅ load ALL store reviews once and build avg map
  useEffect(() => {
    let alive = true;

    async function loadRatings() {
      try {
        // Pull some reviews (increase if you expect a lot)
        const snap = await getDocs(
          query(
            collection(db, "reviews"),
            where("targetType", "==", "store"),
            limit(1000)
          )
        );

        const bucket = {}; // storeId -> { sum, count }

        snap.docs.forEach((d) => {
          const r = d.data() || {};
          const storeId = safe(r.targetId);
          if (!storeId) return;

          const rating = Number(r.rating || 0);
          if (!Number.isFinite(rating)) return;

          if (!bucket[storeId]) bucket[storeId] = { sum: 0, count: 0 };
          bucket[storeId].sum += rating;
          bucket[storeId].count += 1;
        });

        const map = {};
        Object.keys(bucket).forEach((id) => {
          const { sum, count } = bucket[id];
          const avg = count ? Math.round((sum / count) * 10) / 10 : 0; // 1 decimal
          map[id] = { avg, count };
        });

        if (!alive) return;
        setRatingMap(map);
      } catch (e) {
        console.log("loadRatings failed:", e?.message);
        if (!alive) return;
        setRatingMap({});
      }
    }

    loadRatings();
    return () => {
      alive = false;
    };
  }, []);

  // ✅ product search -> find storeIds that have products matching query
  useEffect(() => {
    let alive = true;

    async function searchProducts() {
      const t = safe(qText).toLowerCase();

      if (!t || t.length < 3) {
        if (!alive) return;
        setProductStoreIds(new Set());
        return;
      }

      try {
        const snap = await getDocs(query(collection(db, "products"), limit(200)));

        const ids = new Set();

        snap.docs.forEach((d) => {
          const p = d.data() || {};
          const name = safe(p.name || p.title).toLowerCase();
          const desc = safe(p.description).toLowerCase();
          const cat = safe(p.category || p.productType).toLowerCase();

          const hit = name.includes(t) || desc.includes(t) || cat.includes(t);

          if (hit) {
            const storeId = safe(p.storeId);
            if (storeId) ids.add(storeId);
          }
        });

        if (!alive) return;
        setProductStoreIds(ids);
      } catch {
        if (!alive) return;
        setProductStoreIds(new Set());
      }
    }

    searchProducts();
    return () => {
      alive = false;
    };
  }, [qText]);

  const filteredStores = useMemo(() => {
    const t = safe(qText).toLowerCase();
    let list = stores;

    // text filter for store fields
    if (t) {
      list = list.filter((s) => {
        const name = getStoreName(s).toLowerCase();
        const slug = getStoreSlug(s).toLowerCase();
        const bio = getStoreBio(s).toLowerCase();
        const loc = safe(s.location).toLowerCase();

        const storeHit =
          name.includes(t) || slug.includes(t) || bio.includes(t) || loc.includes(t);

        // ✅ if store itself doesn't match, allow match by products uploaded by that store
        const productHit = productStoreIds.size > 0 && productStoreIds.has(s.id);

        return storeHit || productHit;
      });
    }

    // sorting
    const sorted = [...list];
    if (sort === "AZ") {
      sorted.sort((a, b) => getStoreName(a).localeCompare(getStoreName(b)));
    } else if (sort === "ZA") {
      sorted.sort((a, b) => getStoreName(b).localeCompare(getStoreName(a)));
    } else {
      sorted.sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a));
    }

    return sorted;
  }, [stores, qText, sort, productStoreIds]);

  const S = useMemo(() => {
    const gridCols = isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr";

    return {
      page: {
        minHeight: "100vh",
        background: "#f8fafc",
        padding: isMobile ? 12 : 18,
        color: "#0f172a",
      },
      wrap: { maxWidth: 1280, margin: "0 auto" },

      filterBar: {
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: isMobile ? 10 : 12,
        boxShadow: "0 10px 25px rgba(15,23,42,0.05)",
        display: "grid",
        gap: 10,
      },

      topRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        flexWrap: "wrap",
      },

      topActions: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      },
      linkBtn: {
        border: "1px solid #cbd5e1",
        background: "#fff",
        padding: "9px 10px",
        borderRadius: 12,
        fontWeight: 1000,
        cursor: "pointer",
        minHeight: 38,
        fontSize: 13,
      },
      primaryBtn: {
        border: "none",
        background: "#0f172a",
        color: "#fff",
        padding: "9px 12px",
        borderRadius: 12,
        fontWeight: 1100,
        cursor: "pointer",
        minHeight: 38,
        fontSize: 13,
      },

      controlsRow: {
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr auto",
        gap: 8,
        alignItems: "center",
      },
      control: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 10px",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        background: "#fff",
      },
      input: {
        width: "100%",
        border: "none",
        outline: "none",
        fontSize: 13,
        background: "transparent",
      },
      select: {
        width: "100%",
        border: "none",
        outline: "none",
        fontSize: 13,
        background: "transparent",
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
        fontWeight: 900,
        color: "#0f172a",
        cursor: "pointer",
      },
      selectWrap: { position: "relative", paddingRight: 24 },
      caret: {
        position: "absolute",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        color: "#64748b",
        pointerEvents: "none",
        fontWeight: 1200,
      },
      clearBtn: {
        border: "1px solid #cbd5e1",
        background: "#fff",
        padding: "10px 12px",
        borderRadius: 12,
        fontWeight: 1000,
        cursor: "pointer",
        minHeight: 40,
        fontSize: 13,
        opacity: safe(qText) ? 1 : 0.55,
      },

      grid: {
        marginTop: 12,
        display: "grid",
        gridTemplateColumns: gridCols,
        gap: isMobile ? 10 : 12,
      },

      card: {
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 10px 25px rgba(15,23,42,0.05)",
        cursor: "pointer",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      },

      banner: {
        height: isMobile ? 105 : 115,
        background: "linear-gradient(135deg, #e2e8f0, #ffffff)",
        position: "relative",
      },

      logoWrap: {
        position: "absolute",
        left: 12,
        bottom: -18,
        width: 44,
        height: 44,
        borderRadius: 14,
        border: "1px solid #e2e8f0",
        background: "#fff",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        boxShadow: "0 10px 22px rgba(15,23,42,0.08)",
      },
      logoImg: { width: "100%", height: "100%", objectFit: "cover" },
      logoInitials: { fontWeight: 1200, fontSize: 14 },

      body: {
        padding: "24px 12px 12px 12px",
        display: "grid",
        gap: 5,
      },
      name: { fontWeight: 1200, fontSize: 14, letterSpacing: "-0.02em" },
      meta: {
        fontSize: 11,
        color: "#64748b",
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
      },
      bio: {
        fontSize: 12,
        color: "#334155",
        lineHeight: 1.5,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        minHeight: 34,
      },

      ratingPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid #e2e8f0",
        background: "#fff",
        fontWeight: 1100,
        fontSize: 12,
        width: "fit-content",
      },

      actionsRow: {
        marginTop: 6,
        display: "flex",
        gap: 8,
      },
      actionBtn: {
        flex: 1,
        padding: "8px 10px",
        borderRadius: 999,
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        fontWeight: 1100,
        cursor: "pointer",
        fontSize: 12,
        whiteSpace: "nowrap",
      },
      actionBtnAlt: {
        flex: 1,
        padding: "8px 10px",
        borderRadius: 999,
        border: "1px solid #cbd5e1",
        background: "#ffffff",
        fontWeight: 1100,
        cursor: "pointer",
        fontSize: 12,
        whiteSpace: "nowrap",
      },

      empty: {
        marginTop: 12,
        background: "#fff",
        border: "1px dashed #cbd5e1",
        borderRadius: 16,
        padding: 14,
        color: "#475569",
        fontSize: 13,
      },

      msg: {
        marginTop: 10,
        background: "#fff",
        border: "1px solid #fecaca",
        color: "#991b1b",
        padding: 12,
        borderRadius: 14,
      },
    };
  }, [isMobile, qText]);

  const sortOptions = useMemo(
    () => [
      { value: "NEWEST", label: "Sort: Newest" },
      { value: "AZ", label: "Sort: A–Z" },
      { value: "ZA", label: "Sort: Z–A" },
    ],
    []
  );

  const openStore = (s) => {
    const slug = getStoreSlug(s);
    if (!slug) return;
    navigate(`/s/${slug}`);
  };

  const openReviews = (storeId) => {
    if (!storeId) return;
    navigate(`/reviews/store/${storeId}`);
  };

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        {/* FILTER BAR */}
        <div style={S.filterBar}>
          <div style={S.topRow}>
            <div />
            <div style={S.topActions}>
              <button style={S.linkBtn} onClick={() => navigate("/me")}>
                My Account
              </button>

              <button style={S.linkBtn} onClick={() => navigate("/earn")}>
                Earn Money
              </button>

              <button style={S.linkBtn} onClick={() => navigate("/contactus")}>
                Contact Us
              </button>

              <button style={S.primaryBtn} onClick={() => navigate("/choose")}>
                Create a Store
              </button>
            </div>
          </div>

          <div style={S.controlsRow}>
            <div style={S.control}>
              <span style={{ fontWeight: 1000, color: "#64748b" }}>🔎</span>
              <input
                style={S.input}
                placeholder="Search stores (name, location)… or products (phone, shoes)…"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
            </div>

            {isMobile ? (
              <MobileSheetSelect
                label="Sort"
                value={sort}
                onChange={(v) => setSort(String(v))}
                options={sortOptions}
                placeholder="Sort"
                S={S}
              />
            ) : (
              <div style={{ ...S.control, ...S.selectWrap }}>
                <select
                  style={S.select}
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  <option value="NEWEST">Sort: Newest</option>
                  <option value="AZ">Sort: A–Z</option>
                  <option value="ZA">Sort: Z–A</option>
                </select>
                <span style={S.caret}>▾</span>
              </div>
            )}

            <button
              style={S.clearBtn}
              onClick={() => setQText("")}
              disabled={!safe(qText)}
              title="Clear search"
            >
              Clear
            </button>
          </div>
        </div>

        {msg ? <div style={S.msg}>{msg}</div> : null}

        {loading ? (
          <div style={S.empty}>Loading stores…</div>
        ) : filteredStores.length === 0 ? (
          <div style={S.empty}>
            No stores found. Try searching product names too (e.g. “phone”, “shoe”, “laptop”).
          </div>
        ) : (
          <div style={S.grid}>
            {filteredStores.map((s) => {
              const name = getStoreName(s);
              const slug = getStoreSlug(s);
              const bio = getStoreBio(s);
              const bannerUrl = safe(s.bannerUrl);
              const logoUrl = safe(s.logoUrl);
              const location = safe(s.location);

              const info = ratingMap?.[s.id] || { avg: 0, count: 0 };
              const displayAvg = Number(info.avg || 0).toFixed(1);

              return (
                <div
                  key={s.id}
                  style={S.card}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open store ${name}`}
                  onClick={() => openStore(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openStore(s);
                  }}
                >
                  <div style={S.banner}>
                    {bannerUrl ? (
                      <img
                        src={bannerUrl}
                        alt=""
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : null}

                    <div style={S.logoWrap}>
                      {logoUrl ? (
                        <img src={logoUrl} alt="" style={S.logoImg} />
                      ) : (
                        <div style={S.logoInitials}>{initials(name)}</div>
                      )}
                    </div>
                  </div>

                  <div style={S.body}>
                    <div style={S.name}>{name}</div>

                    <div style={S.meta}>
                      {slug ? <span>🔗 {slug}</span> : null}
                      {location ? <span>📍 {location}</span> : null}
                    </div>

                    <div style={S.ratingPill}>
                      ⭐ {displayAvg} / 5{info.count ? ` (${info.count})` : ""}
                    </div>

                    <div style={S.bio}>{bio}</div>

                    {/* ✅ SIDE-BY-SIDE BUTTONS */}
                    <div style={S.actionsRow}>
                      <button
                        style={S.actionBtnAlt}
                        onClick={(e) => {
                          e.stopPropagation();
                          openReviews(s.id);
                        }}
                      >
                        Reviews →
                      </button>

                      <button
                        style={S.actionBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          openStore(s);
                        }}
                      >
                        View store →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ height: 24 }} />
      </div>
    </div>
  );
}
